import { Injectable, signal, computed, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { updatePrimaryPalette } from '@primeuix/themes';
import {
  generatePalette,
  getDefaultPalette,
  ColorPalette,
  DEFAULT_SEMANTIC_COLORS,
} from '../utils/color-palette.util';

export type Theme = 'light' | 'dark' | 'system';

/** Neutral meta theme-color values that match page background to avoid color flash on load */
const META_THEME_LIGHT = '#ffffff';
const META_THEME_DARK = '#09090b'; // surface-950

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private readonly STORAGE_KEY = 'qwentro-theme';

  // Internal theme state
  private _theme = signal<Theme>(this.getInitialTheme());
  private _systemPrefersDark = signal<boolean>(false);
  private _publicOverride = signal<Theme | null>(null);

  // Organization color state
  private _orgPrimaryColor = signal<string | undefined>(undefined);
  private _orgSuccessColor = signal<string | undefined>(undefined);
  private _orgWarningColor = signal<string | undefined>(undefined);
  private _orgDangerColor = signal<string | undefined>(undefined);
  private _orgInfoColor = signal<string | undefined>(undefined);

  // Public computed properties
  theme = this._theme.asReadonly();
  systemPrefersDark = this._systemPrefersDark.asReadonly();

  // Computed current theme (resolves 'system' to actual theme)
  // When a public override is active, it takes precedence over localStorage preference
  currentTheme = computed(() => {
    const override = this._publicOverride();
    const theme = override ?? this._theme();
    if (theme === 'system') {
      return this._systemPrefersDark() ? 'dark' : 'light';
    }
    return theme;
  });

  // Computed CSS class for theme
  themeClass = computed(() => this.currentTheme());

  // Computed boolean for dark mode
  isDark = computed(() => this.currentTheme() === 'dark');

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeSystemThemeDetection();
      this.initializeMetaThemeColor();
      this.setupThemePersistence();
      this.setupColorReapplicationOnThemeChange();
    }
  }

  /**
   * Set the theme preference
   */
  setTheme(theme: Theme): void {
    this._theme.set(theme);
  }

  /**
   * Toggle between light and dark themes (skips system)
   */
  toggleTheme(): void {
    const current = this._theme();
    if (current === 'system') {
      // If system, toggle to opposite of current system preference
      this._theme.set(this._systemPrefersDark() ? 'light' : 'dark');
    } else {
      // Toggle between light and dark
      this._theme.set(current === 'light' ? 'dark' : 'light');
    }
  }

  /**
   * Set a theme override for public/customer-facing pages.
   * When active, this takes precedence over the user's localStorage preference.
   */
  setPublicThemeOverride(theme: Theme): void {
    this._publicOverride.set(theme);
  }

  /**
   * Clear the public theme override, restoring the user's localStorage preference.
   */
  clearPublicThemeOverride(): void {
    this._publicOverride.set(null);
  }

  /**
   * Get theme-aware color with fallbacks
   */
  getThemeColor(lightColor: string, darkColor: string): string {
    return this.isDark() ? darkColor : lightColor;
  }

  /**
   * Get theme-aware default colors for business card components
   */
  getDefaultColors(): Record<string, string> {
    // Use organization colors if available, otherwise use defaults
    const primaryColor = this._orgPrimaryColor() || (this.isDark() ? '#fbbf24' : '#f59e0b');
    const successColor = this._orgSuccessColor() || DEFAULT_SEMANTIC_COLORS.success;
    const warningColor = this._orgWarningColor() || DEFAULT_SEMANTIC_COLORS.warning;
    const dangerColor = this._orgDangerColor() || DEFAULT_SEMANTIC_COLORS.danger;
    const infoColor = this._orgInfoColor() || DEFAULT_SEMANTIC_COLORS.info;

    if (this.isDark()) {
      return {
        background: '#1a1a1a',
        surface: '#2d2d2d',
        text: '#ffffff',
        textSecondary: '#e5e5e5',
        textMuted: '#a3a3a3',
        border: '#404040',
        primary: primaryColor,
        success: successColor,
        warning: warningColor,
        error: dangerColor,
        info: infoColor,
      };
    } else {
      return {
        background: '#ffffff',
        surface: '#f9fafb',
        text: '#1f2937',
        textSecondary: '#6b7280',
        textMuted: '#9ca3af',
        border: '#e5e7eb',
        primary: primaryColor,
        success: successColor,
        warning: warningColor,
        error: dangerColor,
        info: infoColor,
      };
    }
  }

  /**
   * Apply organization-specific brand colors
   * This dynamically updates PrimeNG's semantic color palettes at runtime
   *
   * @param colors Object containing hex colors for primary, success, warning, danger, and info
   */
  applyOrganizationColors(colors?: {
    primary?: string;
    success?: string;
    warning?: string;
    danger?: string;
    info?: string;
  }): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Store colors in signals
    this._orgPrimaryColor.set(colors?.primary);
    this._orgSuccessColor.set(colors?.success);
    this._orgWarningColor.set(colors?.warning);
    this._orgDangerColor.set(colors?.danger);
    this._orgInfoColor.set(colors?.info);

    // Helper to validate hex color
    const isValidColor = (color: string | undefined): boolean => {
      return !!color && /^#[0-9A-Fa-f]{6}$/.test(color);
    };

    // Generate palettes for each color (use defaults if not provided or invalid)
    const primaryPalette = isValidColor(colors?.primary)
      ? generatePalette(colors!.primary!)
      : getDefaultPalette();

    const successPalette = isValidColor(colors?.success)
      ? generatePalette(colors!.success!)
      : generatePalette(DEFAULT_SEMANTIC_COLORS.success);

    const warningPalette = isValidColor(colors?.warning)
      ? generatePalette(colors!.warning!)
      : generatePalette(DEFAULT_SEMANTIC_COLORS.warning);

    const dangerPalette = isValidColor(colors?.danger)
      ? generatePalette(colors!.danger!)
      : generatePalette(DEFAULT_SEMANTIC_COLORS.danger);

    const infoPalette = isValidColor(colors?.info)
      ? generatePalette(colors!.info!)
      : generatePalette(DEFAULT_SEMANTIC_COLORS.info);

    // NOTE: Do NOT invert palette for dark mode!
    // PrimeNG's design system uses the same shade numbering in both modes:
    // - 50 = lightest, 950 = darkest (in both light and dark mode)
    // - Dark mode components automatically choose appropriate shades (e.g., using 400 instead of 600)
    // Inverting causes 50 to be dark and 950 to be light, which breaks the design system.

    // Update PrimeNG's primary palette via runtime API
    try {
      updatePrimaryPalette(primaryPalette);
    } catch {
      // Failed to update PrimeNG palette
    }

    // Apply all semantic colors via CSS variables
    this.applySemanticColors({
      primary: primaryPalette,
      success: successPalette,
      warning: warningPalette,
      danger: dangerPalette,
      info: infoPalette,
    });

    // Update browser chrome color to match org's primary color
    this.updateMetaThemeColor(isValidColor(colors?.primary) ? colors!.primary! : '#2563EB');
  }

  /**
   * Reset to default theme colors
   */
  resetToDefaultTheme(): void {
    this.applyOrganizationColors({
      primary: undefined,
      success: undefined,
      warning: undefined,
      danger: undefined,
      info: undefined,
    });
  }

  /**
   * Get current organization colors
   */
  getCurrentOrgColors(): {
    primary: string | undefined;
    success: string | undefined;
    warning: string | undefined;
    danger: string | undefined;
    info: string | undefined;
  } {
    return {
      primary: this._orgPrimaryColor(),
      success: this._orgSuccessColor(),
      warning: this._orgWarningColor(),
      danger: this._orgDangerColor(),
      info: this._orgInfoColor(),
    };
  }

  /**
   * Apply semantic CSS variables for PrimeNG theming
   *
   * SIMPLIFIED APPROACH: Update semantic tokens for all color types.
   *
   * PrimeNG's three-tier token system:
   * 1. Primitive tokens: --p-primary-50 through --p-primary-950 (updated by updatePrimaryPalette)
   * 2. Semantic tokens: --p-primary-color, --p-green-600, etc. (WE UPDATE THESE)
   * 3. Component tokens: --p-button-primary-background, etc. (reference semantic tokens)
   *
   * By updating semantic tokens, ALL components that reference them are automatically updated.
   *
   * @see https://primeng.org/theming/styled
   * @see https://github.com/primefaces/primeng/issues/16387
   * @see https://github.com/primefaces/primeng/issues/17307
   */
  private applySemanticColors(palettes: {
    primary: ColorPalette;
    success: ColorPalette;
    warning: ColorPalette;
    danger: ColorPalette;
    info: ColorPalette;
  }): void {
    const root = document.documentElement;

    // PRIMARY semantic tokens (all primary components use these)
    root.style.setProperty('--p-primary-color', palettes.primary[600]);
    root.style.setProperty('--p-primary-contrast-color', '#ffffff');
    root.style.setProperty('--p-primary-hover-color', palettes.primary[700]);
    root.style.setProperty('--p-primary-active-color', palettes.primary[800]);
    root.style.setProperty('--p-focus-ring-color', palettes.primary[600]);
    root.style.setProperty('--p-primary-50', palettes.primary[50]);
    root.style.setProperty('--p-primary-100', palettes.primary[100]);
    root.style.setProperty('--p-primary-200', palettes.primary[200]);

    // SUCCESS - Button component variables
    root.style.setProperty('--p-button-success-background', palettes.success[600]);
    root.style.setProperty('--p-button-success-hover-background', palettes.success[700]);
    root.style.setProperty('--p-button-success-active-background', palettes.success[800]);
    root.style.setProperty('--p-button-success-border-color', palettes.success[600]);
    root.style.setProperty('--p-button-success-hover-border-color', palettes.success[700]);
    root.style.setProperty('--p-button-success-active-border-color', palettes.success[800]);
    root.style.setProperty('--p-button-success-color', '#ffffff');
    root.style.setProperty('--p-button-success-hover-color', '#ffffff');
    root.style.setProperty('--p-button-success-active-color', '#ffffff');
    root.style.setProperty('--p-button-success-text-color', palettes.success[600]);
    root.style.setProperty('--p-button-success-text-hover-background', palettes.success[50]);
    root.style.setProperty('--p-button-success-text-active-background', palettes.success[100]);
    root.style.setProperty('--p-button-text-success-color', palettes.success[600]); // Alias for backward compatibility
    // Message component variables
    root.style.setProperty('--p-message-success-background', palettes.success[50]);
    root.style.setProperty('--p-message-success-border-color', palettes.success[200]);
    root.style.setProperty('--p-message-success-color', palettes.success[700]);
    root.style.setProperty('--p-message-success-icon-color', palettes.success[700]);

    // WARNING - Button component variables
    root.style.setProperty('--p-button-warn-background', palettes.warning[600]);
    root.style.setProperty('--p-button-warn-hover-background', palettes.warning[700]);
    root.style.setProperty('--p-button-warn-active-background', palettes.warning[800]);
    root.style.setProperty('--p-button-warn-border-color', palettes.warning[600]);
    root.style.setProperty('--p-button-warn-hover-border-color', palettes.warning[700]);
    root.style.setProperty('--p-button-warn-active-border-color', palettes.warning[800]);
    root.style.setProperty('--p-button-warn-color', '#ffffff');
    root.style.setProperty('--p-button-warn-hover-color', '#ffffff');
    root.style.setProperty('--p-button-warn-active-color', '#ffffff');
    root.style.setProperty('--p-button-warn-text-color', palettes.warning[600]);
    root.style.setProperty('--p-button-warn-text-hover-background', palettes.warning[50]);
    root.style.setProperty('--p-button-warn-text-active-background', palettes.warning[100]);
    root.style.setProperty('--p-button-text-warn-color', palettes.warning[600]); // Alias for backward compatibility
    // Message component variables
    root.style.setProperty('--p-message-warn-background', palettes.warning[50]);
    root.style.setProperty('--p-message-warn-border-color', palettes.warning[200]);
    root.style.setProperty('--p-message-warn-color', palettes.warning[700]);
    root.style.setProperty('--p-message-warn-icon-color', palettes.warning[700]);

    // DANGER - Button component variables
    root.style.setProperty('--p-button-danger-background', palettes.danger[600]);
    root.style.setProperty('--p-button-danger-hover-background', palettes.danger[700]);
    root.style.setProperty('--p-button-danger-active-background', palettes.danger[800]);
    root.style.setProperty('--p-button-danger-border-color', palettes.danger[600]);
    root.style.setProperty('--p-button-danger-hover-border-color', palettes.danger[700]);
    root.style.setProperty('--p-button-danger-active-border-color', palettes.danger[800]);
    root.style.setProperty('--p-button-danger-color', '#ffffff');
    root.style.setProperty('--p-button-danger-hover-color', '#ffffff');
    root.style.setProperty('--p-button-danger-active-color', '#ffffff');
    root.style.setProperty('--p-button-danger-text-color', palettes.danger[600]);
    root.style.setProperty('--p-button-danger-text-hover-background', palettes.danger[50]);
    root.style.setProperty('--p-button-danger-text-active-background', palettes.danger[100]);
    root.style.setProperty('--p-button-text-danger-color', palettes.danger[600]); // Alias for backward compatibility
    // Message component variables
    root.style.setProperty('--p-message-error-background', palettes.danger[50]);
    root.style.setProperty('--p-message-error-border-color', palettes.danger[200]);
    root.style.setProperty('--p-message-error-color', palettes.danger[700]);
    root.style.setProperty('--p-message-error-icon-color', palettes.danger[700]);

    // INFO - Button component variables
    root.style.setProperty('--p-button-info-background', palettes.info[600]);
    root.style.setProperty('--p-button-info-hover-background', palettes.info[700]);
    root.style.setProperty('--p-button-info-active-background', palettes.info[800]);
    root.style.setProperty('--p-button-info-border-color', palettes.info[600]);
    root.style.setProperty('--p-button-info-hover-border-color', palettes.info[700]);
    root.style.setProperty('--p-button-info-active-border-color', palettes.info[800]);
    root.style.setProperty('--p-button-info-color', '#ffffff');
    root.style.setProperty('--p-button-info-hover-color', '#ffffff');
    root.style.setProperty('--p-button-info-active-color', '#ffffff');
    root.style.setProperty('--p-button-info-text-color', palettes.info[600]);
    root.style.setProperty('--p-button-info-text-hover-background', palettes.info[50]);
    root.style.setProperty('--p-button-info-text-active-background', palettes.info[100]);
    root.style.setProperty('--p-button-text-info-color', palettes.info[600]); // Alias for backward compatibility
    // Message component variables
    root.style.setProperty('--p-message-info-background', palettes.info[50]);
    root.style.setProperty('--p-message-info-border-color', palettes.info[200]);
    root.style.setProperty('--p-message-info-color', palettes.info[700]);
    root.style.setProperty('--p-message-info-icon-color', palettes.info[700]);

    // SET RAW TAILWIND VARIABLES (for bg-success-500, text-danger-600, etc.)
    const setTailwindVariables = (name: string, palette: ColorPalette): void => {
      // Set individual shades: --success-50, --success-100...
      Object.keys(palette).forEach((key) => {
        root.style.setProperty(`--${name}-${key}`, palette[key as unknown as keyof ColorPalette]);
      });
      // Set default (usually 500): --success
      root.style.setProperty(`--${name}`, palette[500]);
    };

    setTailwindVariables('primary', palettes.primary);
    setTailwindVariables('success', palettes.success);
    setTailwindVariables('warning', palettes.warning);
    setTailwindVariables('danger', palettes.danger);
    setTailwindVariables('info', palettes.info);
  }

  /** Get the neutral meta theme-color for the current light/dark mode */
  private getNeutralMetaThemeColor(): string {
    return this.isDark() ? META_THEME_DARK : META_THEME_LIGHT;
  }

  /** Set meta theme-color to match light/dark mode immediately on init (before org loads) */
  private initializeMetaThemeColor(): void {
    this.updateMetaThemeColor(this.getNeutralMetaThemeColor());
  }

  private updateMetaThemeColor(color: string): void {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
  }

  private getInitialTheme(): Theme {
    if (!isPlatformBrowser(this.platformId)) {
      return 'light';
    }

    // Try to get from localStorage first
    const stored = localStorage.getItem(this.STORAGE_KEY) as Theme;
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored;
    }

    // Default to light theme
    return 'light';
  }

  private initializeSystemThemeDetection(): void {
    // Set initial system preference
    this._systemPrefersDark.set(window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => {
      this._systemPrefersDark.set(e.matches);
    };

    mediaQuery.addEventListener('change', handler);

    // Cleanup is handled automatically by Angular's DI system
  }

  private setupThemePersistence(): void {
    // Save theme changes to localStorage
    effect(() => {
      const theme = this._theme();
      localStorage.setItem(this.STORAGE_KEY, theme);
    });
  }

  private setupColorReapplicationOnThemeChange(): void {
    // Re-apply organization colors when theme changes (light/dark toggle)
    effect(() => {
      this.currentTheme(); // This triggers on light/dark change
      const primaryColor = this._orgPrimaryColor();

      // Only re-apply if we have organization colors set
      if (primaryColor) {
        this.applyOrganizationColors({
          primary: primaryColor,
          success: this._orgSuccessColor(),
          warning: this._orgWarningColor(),
          danger: this._orgDangerColor(),
          info: this._orgInfoColor(),
        });
      }
    });
  }
}
